<template>
  <v-dialog class="modal" persistent width="900" v-model="internal">
    <v-card>
      <v-card-actions class="d-flex justify-space-between">
        <v-card-title>{{$t('instructions.uploadFiles.documentation')}}&nbsp; <i>{{ steps > 1 ? ' ' + operationAlias : '' }}</i></v-card-title>
        <v-btn icon data-testid="upload-close" @click="closeModal"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-actions>
      <!--progress line-->
      <v-stepper  v-model="steps">
        <v-stepper-header class="px-4">
          <template v-for="i in totalSteps">
            <v-stepper-step :complete="steps > i" :step="i"></v-stepper-step>
          </template>
        </v-stepper-header>
        <v-stepper-items>
          <!--add alias-->
            <v-stepper-content :step="1">
              <h3 class="text-center">{{$t('instructions.uploadFiles.newOperation')}}</h3>
                <v-text-field
                v-model="operationAlias"
                data-testid="upload-alias"
                maxLength="25"
                :counter="25"
                :label="$t('quoteInfo.alias')"
                ></v-text-field>
            </v-stepper-content>
            
            <!--upload files-->
            <v-stepper-content :step="2">
              <v-card-actions
              class="d-flex flex-column align-center my-5 justify-center text-center"
              >
                <h3>{{$t('instructions.uploadFiles.uploadHere')}}</h3>
              </v-card-actions>
              <v-card-text>
                <file-drop-upload
                  v-model="files.docs"
                  :max-size="maxOperationFileSize"
                  @error="onUploadError"
                />
              </v-card-text>
              </v-stepper-content>
            <v-stepper-content :step="3">
              <v-form ref="form">
                <v-card class="ma-4 pa-5 position-relative" outlined>
              <div
                class="d-flex justify-space-between align-center my-2"
                 v-for="([label, key], index) in Object.entries($t('instructions.uploadFiles.typesOfDocumentation'))"
                :key="key+index"
              >
                <div class="d-flex flex-row align-center">
                  <h3 class="mr-3">{{key}}</h3>
                  <v-chip outlined small color="red" v-show="index==0">{{$t('instructions.uploadFiles.obligatory')}}</v-chip>
                </div>

                <v-select
                  style="max-width: 200px"
                  v-model="selectedFilesInputs.juridica[index]"
                  :data-testid="`upload-doc-${index}`"
                  :items="docFileNames"
                  outlined
                  dense
                />
              </div>
          </v-card>
        </v-form>
            </v-stepper-content>
      <v-stepper-content :step="4" data-testid="upload-result" :data-state="uploadState">
          <v-card
            v-if="uploadState === 'loading'"
            elevation="0"
            class="d-flex flex-column align-center pa-8"
          >
            <v-progress-circular indeterminate color="primary" size="100"></v-progress-circular>
            <div class="text-h6 mt-6">{{$t('instructions.uploadFiles.processing')}}</div>
          </v-card>
          <v-card v-else-if="uploadState === 'success'" elevation="0" class="ma-4 text-center upload-success">
            <div class="mx-auto d-flex flex-column align-center">
              <div class="success-icon-ring mb-4">
                <v-icon color="success" size="54">mdi-check-circle</v-icon>
              </div>
              <div class="text-h5 font-weight-bold mb-2">
                {{$t('instructions.uploadFiles.operationCreated')}}
              </div>
              <div class="text-body-1 grey--text text--lighten-1 success-copy">
                {{$t('instructions.uploadFiles.awaitingApproval')}}
              </div>
            </div>

            <operation-approval-progress class="my-8" :operation-alias="operationAlias" />

            <!-- <v-btn color="primary" large min-width="160" @click="closeModal">
              {{$t('instructions.close')}}
            </v-btn> -->
          </v-card>
          <v-card v-else-if="uploadState === 'refused'" elevation="0" class="ma-5 pa-6 text-center">
            <v-icon color="error" size="54" class="mb-3">mdi-alert-circle-outline</v-icon>
            <div class="text-h6 mb-5">{{$t('instructions.uploadFiles.messages.noBankToApprove')}}</div>
          </v-card>
          <v-card v-else elevation="0" class="ma-5 pa-6 text-center">
            <v-icon color="error" size="54" class="mb-3">mdi-alert-circle-outline</v-icon>
            <div class="text-h6 mb-5">{{$t('instructions.uploadFiles.messages.requestFailed')}}</div>
          </v-card>
            </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
      <div 
        v-if="steps !== 4"
        class="d-flex justify-end flex-gap-2 pa-5">
        <v-btn
        :disabled="steps <= 1"
         @click="steps--"
         class="elevation-0"
         >
         {{$t('instructions.back')}}</v-btn>

        <v-btn
          :disabled="handleDisabledButton"
          data-testid="upload-next"
          @click="nextPage"
          color="primary"
         class="elevation-0"
          >{{$t('instructions.next')}}</v-btn
        >
      </div>
        <!-- GLOBAL SNACKBAR -->
        <v-snackbar
          v-model="globalSnackbar.show"
          :timeout="4000"
          :color="globalSnackbar.color"
          bottom
        >
          <div class="black--text text-center w-100">
            {{ globalSnackbar.message }}
          </div>
        </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import { useExecutionStore, MAX_OPERATION_FILE_SIZE } from "@/store";
import { useInteractionEmailStore } from "@/store/interactions";
import { mapStores } from "pinia";
import { nomalizeFileTitle } from "@/utils";
import OperationApprovalProgress from '@/components/operations/OperationApprovalProgress.vue'
import FileDropUpload from '@/components/common/FileDropUpload.vue'

export default {
  name: "UploadFilesModal",
  components: { OperationApprovalProgress, FileDropUpload },
  props: { 
    operationList: { type: Array, default: [] }, 
    value: { type: Boolean, default: false }, 
    rfqId:{type:String, default: ''}, 
    clientId :{type: String, default: null }, 
    operationDetails:{type: Object, default : ()=>({})},
    brokerEmail:{type:String, default:''},
    masterEmail: { type: String, default: '' },
    operationType :{type: String, default: null }, 
    brokerName: { type: String, default: '' },
    brokerCnpj: { type: String, default: '' },
    id :{type: String, default: '' } 
  },
  mounted(){
    
  },
  data() {
    return {
      files: { id: null, docs: [], rfqId : null },
      steps: 1,
      globalSnackbar :{
        show: false,
        color: 'green',
        message: ''
      },
      isUploading: 0,
      uploadState: 'idle',
      operationAlias: '',
      totalSteps:4,
      headerItems: 
        {
          doctype:[
            "invoice",
            "import_declaration",
            "proof_of_import",
            "bill_of_lading"
          ]
        },
      selectedFilesInputs: {
        juridica: Array(4).fill(null),
      },
      selectedPeople: null,
    };
  },
  watch: {
    internal(val) {
      if (!val) {
        this.closeModal();
      }
    },
    value(val) {
    const existedOp = this.operationList.find(li=>li.id === this.id)
    this.operationAlias = existedOp ? existedOp.alias : ''
    this.internal = val;
    },
  },
  computed: {
    ...mapStores(useExecutionStore),
    ...mapStores(useInteractionEmailStore),
    maxOperationFileSize () {
      return MAX_OPERATION_FILE_SIZE
    },
    docFileNames () {
      return this.files.docs.map(f => f.name)
    },
    internal: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    handleDisabledButton()
    {
        if (this.steps === 1) {
          return !this.operationAlias
        }

        if (this.steps === 2) {
          return this.files.docs.length === 0
        }

        if (this.steps === 3) {
          return !this.selectedFilesInputs.juridica[0]
        }

        return false
    }
  },
  methods: {
    onUploadError (message) {
      this.globalSnackbar.show = true
      this.globalSnackbar.color = 'error'
      this.globalSnackbar.message = message
    },
    parseStorageValue(rawValue) {
      if (rawValue === null || rawValue === undefined) {
        return ''
      }
      const rawText = String(rawValue).trim()
      if (!rawText) {
        return ''
      }
      try {
        const parsed = JSON.parse(rawText)
        if (typeof parsed === 'string' || typeof parsed === 'number') {
          return String(parsed).trim()
        }
        return rawText
      } catch (error) {
        return rawText
      }
    },
    async uploadSelectedFiles() {
      let operationId = null
      let uploadedCount = 0
      this.uploadState = 'loading'
      try {
        for (const [index, name] of this.selectedFilesInputs.juridica.entries()) {
          if (!name) {
            continue
          }

          let fileByName = this.files.docs.find(doc => doc.name === name)
          if (!fileByName) {
            console.warn(`Skipping file not found: ${name}`)
            continue
          }
         
          const newFile = nomalizeFileTitle(fileByName)
          const response = await this.executionStore.uploadOperation(
            this.operationAlias,
            this.headerItems.doctype[index],
            newFile,
            false,
            operationId,
            this.operationDetails.client,
            this.operationDetails.remessa,
            // this.operationDetails.volume
          )
          this.isUploading++
          uploadedCount++

          const responseData = response?.data || {}
          operationId = responseData.operation_id || responseData?.data?.operation_id || operationId
          if (operationId && String(localStorage.getItem('userRole') || '').toLowerCase() === 'pro') {
            this.rememberSelfCreatedOperation(operationId)
          }
        }

        if (!uploadedCount) {
          this.uploadState = 'error'
          this.globalSnackbar.show = true
          this.globalSnackbar.color = 'error'
          this.globalSnackbar.message = this.$t('instructions.uploadFiles.messages.validationError')
          return
        }

        if (!operationId) {
          this.uploadState = 'error'
          this.globalSnackbar.show = true
          this.globalSnackbar.color = 'error'
          this.globalSnackbar.message = this.$t('instructions.uploadFiles.messages.requestFailed')
          return
        }


        // The documents are stored and the operation exists by this point, so a
        // failure here is not an upload failure. Reporting it as one sends the
        // user back through the wizard, which creates a *second* operation and a
        // second copy of every document. The approval mail can be resent from
        // the operation; a duplicate cannot be undone.
        const brokerId = localStorage.getItem('brokerId')
        try {
          await this.interactionEmailStore.requestOperationApproval({
            operationId,
            brokerId
          })
        } catch (error) {
          console.error('Documents uploaded, but failed to deliver approval email', error)
        }

        this.globalSnackbar.show = true
        this.globalSnackbar.color = 'success'
        this.globalSnackbar.message = this.$t('instructions.uploadFiles.messages.success')
        this.uploadState = 'success'
        this.$emit('wasFileUploaded', true)
      } catch (error) {
        console.error('Failed to upload documents', error)
        // The platform refuses an operation nobody can approve by e-mail: the
        // client has no bank with an address. Nothing was stored, and trying
        // again changes nothing until a bank is linked.
        const response = error?.response
        const refused = response?.status === 422 && /no bank/i.test(String(response?.data?.detail || ''))
        this.globalSnackbar.show = true
        this.globalSnackbar.color = 'error'
        this.globalSnackbar.message = this.$t(refused
          ? 'instructions.uploadFiles.messages.noBankToApprove'
          : 'instructions.uploadFiles.messages.requestFailed')
        this.uploadState = refused ? 'refused' : 'error'
      }
    },
    async nextPage() {
      this.steps++;
      if (!this.selectedFilesInputs) return;
      if (this.steps === 3) {
        const valid = this.$refs.form.validate();
        if (!valid) return;
      }
      if(this.steps === 4 )
      {
        await this.uploadSelectedFiles()
      }
      if (this.steps > 4 && this.isUploading > 1) this.closeModal();
    },
    closeModal() {
      this.selectedFilesInputs = {
        juridica: Array(4).fill(null),
      };
      const info = {
        alias : this.operationAlias,
        docs : this.files.docs.length
      }
      this.executionStore.selectedId= null
      this.files = {id: null, docs : [], rfqId: null}
      this.steps = 1;
      this.$emit('operationInfo', info)
      this.operationAlias = ''
      this.isUploading = 0
      this.uploadState = 'idle'
      this.$emit("input", false);
    },
    rememberSelfCreatedOperation (operationId) {
      let ids = []
      try {
        ids = JSON.parse(localStorage.getItem('selfCreatedOperationIds') || '[]').map(String)
      } catch {
        ids = []
      }

      const id = String(operationId)
      if (!ids.includes(id)) {
        localStorage.setItem('selfCreatedOperationIds', JSON.stringify([...ids, id].slice(-100)))
      }
      window.dispatchEvent(new CustomEvent('operation-created-by-current-user', {
        detail: { operationId: id }
      }))
    },

  },
};
</script>
<style scoped>
.mapped-fields {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  align-items: center;
  justify-content: center;
}

::v-deep .v-stepper__header {
  box-shadow: none !important;
}
::v-deep .theme--dark.v-stepper{
  background: transparent !important;
  box-shadow: none !important;
}

::v-deep .v-stepper__content{
  padding: .5rem  2rem !important;
}
.upload-success {
  max-width: 720px;
  margin-left: auto !important;
  margin-right: auto !important;
}
.success-icon-ring {
  align-items: center;
  background: rgba(76, 175, 80, 0.12);
  border-radius: 50%;
  display: flex;
  height: 82px;
  justify-content: center;
  width: 82px;
}
.success-copy {
  max-width: 560px;
}
</style>
